import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import Group from '@/ui/components/Header/SideNavigation/Group/Group'
import Item from '@/ui/components/Header/SideNavigation/Item/Item'
import Sublabel from '@/ui/components/Header/SideNavigation/Sublabel/Sublabel'

import { MenuItemTypes } from '@/constants'
import { RootState } from '@/utils/store/store'

import './SideNavigation.scss'

import { SideNavigationMenuItem as MenuItem, SideNavigationProps } from './types/SideNavigationTypes'
import { useTranslation } from 'react-i18next'

const SideNavigation = ({ menuGroups, isMenuOpen, toggleMenu }: SideNavigationProps) => {
    const { config } = useSelector((state: RootState) => state.application)
    const { i18n } = useTranslation()
    // The highlight moves with the click: an action can take a while (the assistant is switched first), the location
    // only changes after it
    const [pendingPath, setPendingPath] = useState<string>()
    const { pathname } = useLocation()
    const clickRef = useRef(0)
    const fallbackTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    // The location is the end of it: the router can commit it a moment after the action is done, and clearing before
    // would show the old item for a frame
    useEffect(() => {
        if (pendingPath && pathname === pendingPath) setPendingPath(undefined)
    }, [pathname, pendingPath])

    useEffect(() => () => clearTimeout(fallbackTimer.current), [])

    const renderMenuComponent = (component: MenuItem, key: number) => {
        let renderedComponent = null

        switch (component.type) {
            case MenuItemTypes.item:
                renderedComponent = renderItem(component, key)
                break
            case MenuItemTypes.group:
                renderedComponent = renderGroup(component, key)
                break
            case MenuItemTypes.sublabel:
                renderedComponent = renderSublabel(component, key)
                break
            default:
                console.warn(`Warning: ${component.type} is not a valid menu item type`)
        }

        return renderedComponent
    }

    const renderItem = (item: MenuItem, key: number) => {
        const { label } = item
        const lang = i18n.language
        const tooltipLabel = item.translations?.[lang]?.name || label

        return (
            <Item
                key={key}
                isMenuOpen={isMenuOpen}
                toggleMenu={toggleMenu}
                isActive={item.pathHighlight}
                label={item.label ?? ''}
                menuItemTooltipLabel={tooltipLabel}
                icon={item.icon}
                iconSrc={item.iconSrc}
                activePath={pendingPath}
                action={item.action && (async () => {
                    const click = ++clickRef.current
                    clearTimeout(fallbackTimer.current)
                    setPendingPath(item.pathHighlight)
                    try {
                        await item.action?.()
                    } finally {
                        // an action that does not end on this path (failed, or no navigation): give the router a moment,
                        // then the location shows again. Only the last click may do this
                        fallbackTimer.current = setTimeout(() => {
                            if (click === clickRef.current) setPendingPath(undefined)
                        }, 600)
                    }
                })}
                link={item.link}
                badges={item.badges}
            />
        )
    }

    const renderGroup = (group: MenuItem, index: number) => (
        <Group
            key={index}
            isMenuOpen={isMenuOpen}
            openMenu={() => toggleMenu(true)}
            {...group}
        >
            {group.children?.map((child, childIndex) => renderMenuComponent(child, childIndex))}
        </Group>
    )

    const renderSublabel = (sublabel: MenuItem, key: number) => {
        return (
            <Sublabel
                key={key}
                isMenuOpen={isMenuOpen}
                text={sublabel.text ?? sublabel.label ?? ''}
            />
        )
    }

    return (
        <div className='side-navigation'>
            <nav
                className={`m-side-navigation -contrast ${isMenuOpen ? '-open' : ''}`}
                aria-label='Side Navigation'
                aria-hidden='false'
            >
                <div className='m-side-navigation__header'>
                    <div className='m-side-navigation__header__label -size-l highlight'>
                        {config?.name}
                    </div>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label m-side-navigation__header__trigger -open'
                        aria-haspopup='false'
                        aria-label='Open Side Navigation'
                        tabIndex={0}
                        onClick={() => {
                            toggleMenu(true)
                        }}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-list-view-mobile'></i>
                    </button>
                    <button
                        type='button'
                        className='a-button a-button--integrated -without-label m-side-navigation__header__trigger -close'
                        aria-haspopup='false'
                        aria-label='Close Side Navigation'
                        tabIndex={-1}
                        onClick={() => toggleMenu(false)}
                    >
                        <i className='a-icon a-button__icon boschicon-bosch-ic-close'></i>
                    </button>
                </div>

                {menuGroups?.map((group, groupIndex) => {
                    return (
                        <ul key={`menu-group-${groupIndex}`} className={`m-menu-group ${group.customClasses}`} role='menubar' aria-orientation='vertical'>
                            {
                                group.children?.map((item, itemIndex) => {
                                    return renderMenuComponent(item, itemIndex)
                                })
                            }
                        </ul>
                    )
                })}
            </nav>
        </div>
    )
}

export default SideNavigation
